import { createContext, useContext, useState, type ReactElement } from 'react'
import { isDefined, type TeamData } from '@terros/common'
import { useTeamHierarchyContext } from './TeamHierarchyContext'

type DraggedTeam = TeamData & {
  parentLevel?: number
  highestChildLevel?: number
}

type DragState = {
  draggedTeam: DraggedTeam | null
  dragOverTeam: TeamData | null
  dragOverLevel: number | null
}

type TeamDragContextType = {
  dragState: DragState
  startDrag: (team: TeamData, parentLevel?: number, highestChildLevel?: number) => void
  endDrag: () => void
  setDragOverTeam: (team: TeamData | null) => void
  setDragOverLevel: (level: number | null) => void
  canDrop: (targetTeam: TeamData | null, targetLevel: number | null) => boolean
}

const TeamDragContext = createContext<TeamDragContextType | null>(null)

export const TeamDragProvider = ({ children }: { children: ReactElement }): ReactElement => {
  const { company } = useTeamHierarchyContext()
  const [dragState, setDragState] = useState<DragState>({
    draggedTeam: null,
    dragOverTeam: null,
    dragOverLevel: null,
  })

  const startDrag = (team: TeamData, parentLevel?: number, highestChildLevel?: number): void => {
    setDragState((prev) => ({ ...prev, draggedTeam: { ...team, parentLevel, highestChildLevel } }))
  }

  const endDrag = (): void => {
    setDragState({
      draggedTeam: null,
      dragOverTeam: null,
      dragOverLevel: null,
    })
  }

  const setDragOverTeam = (team: TeamData | null): void => {
    setDragState((prev) => ({ ...prev, dragOverTeam: team }))
  }

  const setDragOverLevel = (level: number | null): void => {
    setDragState((prev) => ({ ...prev, dragOverLevel: level }))
  }

  const canDrop = (targetTeam: TeamData | null, targetLevel: number | null): boolean => {
    const { draggedTeam } = dragState
    if (!draggedTeam) return false

    if (targetTeam) {
      // Can't drop into itself
      if (targetTeam.teamId === draggedTeam.teamId) return false
      // Can't drop into its direct parent
      if (targetTeam.teamId === draggedTeam.parentTeamIds?.[0]) return false
      // Can't drop into one of its children
      if (targetTeam.parentTeamIds.includes(draggedTeam.teamId)) return false
      // can't make dragged team same level as its highest child
      if (isDefined(draggedTeam.highestChildLevel) && targetTeam.level - 1 <= draggedTeam.highestChildLevel)
        return false
      // Can't drop into the lowest level team
      if (targetTeam.level === company.lowestTeamLevel) return false
    }

    if (targetLevel !== null) {
      // allow drop into the highest level
      if (company.highestTeamLevel === targetLevel) return true
      // Can't drop into its own level
      if (targetLevel === draggedTeam.level) return false
      // Can't drop into a level higher or equal to the parent level
      if (draggedTeam.parentLevel && targetLevel >= draggedTeam.parentLevel) return false
      // Can't drop into a level that has one of its children
      if (targetLevel <= (draggedTeam.highestChildLevel ?? 0)) return false

      return true
    }

    return true
  }

  return (
    <TeamDragContext.Provider
      value={{
        dragState,
        startDrag,
        endDrag,
        setDragOverTeam,
        setDragOverLevel,
        canDrop,
      }}
    >
      {children}
    </TeamDragContext.Provider>
  )
}

export const useTeamDrag = (): TeamDragContextType => {
  const context = useContext(TeamDragContext)
  if (!context) {
    throw new Error('useTeamDrag must be used within a TeamDragProvider')
  }
  return context
}

const isChild = (parent: TeamData, child: TeamData): boolean => {
  if (parent.teamId === child.teamId) return true
  if (child.parentTeamIds?.includes(parent.teamId)) return true
  return false
}
